/**
 * Order detail screen — read-only view of one order (líneas, entrega,
 * devoluciones). Reached from OrderHistoryPage's row links.
 *
 * While the order is still editable (before cutoff), the header shows the cutoff
 * deadline card with an "Editar pedido" CTA back to the proposal — the two read
 * as one grouped unit. There is no "Historial" section: order events
 * are backoffice concerns, not client-facing.
 *
 * All fetching and shaping lives in useOrderDetail; this file only renders. No
 * local edit state exists here (unlike ProposalPage/OrderHistoryPage), so
 * there's no cross-store leak risk to guard against with a key={} remount —
 * the query itself is keyed by storeId+orderId, so switching either via the
 * route param or the active store naturally re-fetches.
 */
import React from 'react'
import type {CutoffRule} from '@retail/dates'
import {Link, useParams} from 'react-router-dom'
import {formatPostalCode} from '@retail/orders-domain'
import {CutoffCountdown, Tag} from '../../components'
import {useActiveStore} from '../../store/StoreContext'
import {storeCodeSegment} from '../../lib/storeRouting'
import {ordersPath, proposalPath} from '../../lib/routes'
import {mapOrderError} from '../../lib/orderErrors'
import {STATUS_BG, STATUS_LABELS} from '../../lib/orderStatus'
import {useOrderDetail} from './useOrderDetail'
import styles from './OrderDetailPage.module.scss'

export const OrderDetailPage = (): React.ReactElement => {
  const {activeStore} = useActiveStore()

  if (activeStore === undefined) {
    return (
      <div className={styles['order-detail']}>
        <div className={`col-flx flx-a-c flx-j-c flx-gap-12 p-24 txt-a-c bg-white txt-light-kale ${styles['order-detail__state']}`}>Cargando tienda…</div>
      </div>
    )
  }

  return <OrderDetailPageInner storeId={activeStore.id} storeSeg={storeCodeSegment(activeStore)} cutoff={activeStore.cutoff} />
}

type OrderDetailPageInnerProps = {
  storeId: number
  /** The active store's URL segment, so the back/edit links stay store-scoped. */
  storeSeg: string
  /** The store's own cutoff rule; absent = the general one. */
  cutoff?: CutoffRule
}

const OrderDetailPageInner = ({storeId, storeSeg, cutoff}: OrderDetailPageInnerProps): React.ReactElement => {
  const {orderId} = useParams<{storeCode: string; orderId: string}>()
  const viewModel = useOrderDetail({storeId, orderId: Number(orderId), cutoff})

  const ordersTo = ordersPath(storeSeg)

  if (viewModel.isNotFound) {
    return (
      <div className={styles['order-detail']}>
        <div className={`col-flx flx-a-c flx-j-c flx-gap-12 p-24 txt-a-c bg-white txt-light-kale ${styles['order-detail__state']}`}>
          <p>No se encontró el pedido.</p>
          <Link to={ordersTo} className={`m-b-16 txt-decoration-none ${styles['order-detail__back']}`}>← Volver a pedidos</Link>
        </div>
      </div>
    )
  }

  if (viewModel.isError) {
    return (
      <div className={styles['order-detail']}>
        <div className={`col-flx flx-a-c flx-j-c flx-gap-12 p-24 txt-a-c bg-white ${styles['order-detail__state--error']}`}>{mapOrderError(viewModel.error, 'read')}</div>
      </div>
    )
  }

  const {detail} = viewModel
  if (detail === undefined) {
    // Not erroring, but no data yet (initial load, or query still disabled) —
    // render the same loading copy as an explicit isLoading branch would.
    return (
      <div className={styles['order-detail']}>
        <div className={`col-flx flx-a-c flx-j-c flx-gap-12 p-24 txt-a-c bg-white txt-light-kale ${styles['order-detail__state']}`}>Cargando pedido…</div>
      </div>
    )
  }

  const {address} = detail

  return (
    <div className={styles['order-detail']}>
      <div className={`m-v-0 m-h-auto ${styles['order-detail__wrap']}`}>
        <Link to={ordersTo} className={`m-b-16 txt-decoration-none ${styles['order-detail__back']}`}>← Volver a pedidos</Link>

        <div className={`row-flx flx-a-s flx-j-b flx-w m-b-24 ${styles['order-detail__ctx']}`}>
          <div>
            <div className={`m-b-4 txt-bold-500 txt-upper txt-light-kale ${styles['order-detail__overline']}`}>{detail.weekLabel}</div>
            <h1 className={`m-h-0 m-b-0 txt-honey txt-kale ${styles['order-detail__title']}`}>Detalle del pedido</h1>
          </div>
          <Tag label={STATUS_LABELS[detail.status]} bgColor={STATUS_BG[detail.status]} />
        </div>

        {/* Countdown + "Editar pedido" grouped as one full-width bar (button on
            the same line as the countdown) — only while editable. */}
        {detail.isEditable && (
          <div className="m-b-16">
            <CutoffCountdown
              cutoffAt={detail.cutoffAt}
              layout="row"
              action={
                <Link to={proposalPath(storeSeg)} className={`flx-a-c flx-j-c border-xxl txt-white txt-bold-500 bg-olive txt-decoration-none ${styles['order-detail__edit']}`}>
                  Editar pedido
                </Link>
              }
            />
          </div>
        )}

        {/* Líneas */}
        <section className={`bg-white m-b-16 ${styles['order-detail__section']}`}>
          <h2 className={`m-t-0 m-h-0 m-b-12 txt-kale ${styles['order-detail__section-title']}`}>Líneas</h2>
          <div className={`flx-gap-12 ${styles['order-detail__lines-head']}`}>
            <span>Plato</span>
            <span className={`txt-a-r ${styles['order-detail__cell--right']}`}>Unidades</span>
          </div>
          {detail.lines.map((line) => (
            <div key={line.productId} className={`flx-gap-12 txt-kale ${styles['order-detail__lines-row']}`}>
              <span>{line.productName}</span>
              <span className={`txt-a-r ${styles['order-detail__cell--right']}`}>{line.quantity}</span>
            </div>
          ))}
          <div className={`flx-gap-12 txt-kale ${styles['order-detail__lines-total']}`}>
            <span>Total de unidades</span>
            <span className={`txt-a-r ${styles['order-detail__cell--right']}`}>{detail.totalUnits}</span>
          </div>
        </section>

        {/* Entrega */}
        <section className={`bg-white m-b-16 ${styles['order-detail__section']}`}>
          <h2 className={`m-t-0 m-h-0 m-b-12 txt-kale ${styles['order-detail__section-title']}`}>Entrega</h2>
          <div className={`txt-kale ${styles['order-detail__delivery']}`}>
            <p className="m-t-0 m-h-0 m-b-8 txt-bold-500">{detail.deliveryLabel}</p>
            <div className={`col-flx txt-kale ${styles['order-detail__addr']}`}>
              <span>
                {address.street}, {address.number}
                {/* `!= null`: a stored floor can be null as well as absent, and neither prints. */}
                {address.floor != null ? ` ${address.floor}` : ''}
              </span>
              <span>
                {formatPostalCode(address.zipcode)} {address.city}
              </span>
            </div>
            <p className={`m-h-0 m-b-0 m-t-8 txt-light-kale ${styles['order-detail__delivery-phone']}`}>{address.phone}</p>
          </div>
        </section>

        {/* Devoluciones */}
        <section className={`bg-white m-b-16 ${styles['order-detail__section']}`}>
          <h2 className={`m-t-0 m-h-0 m-b-12 txt-kale ${styles['order-detail__section-title']}`}>Devoluciones</h2>
          {detail.returns.length === 0 ? (
            <p className={`m-0 txt-light-kale ${styles['order-detail__empty']}`}>No hay devoluciones registradas.</p>
          ) : (
            <div className="col-flx flx-gap-16">
              {detail.returns.map((returnView) => (
                <div key={returnView.serialNumber} className={`border-s p-v-12 p-h-16 ${styles['order-detail__return']}`}>
                  <div className={`row-flx flx-a-c flx-j-b flx-gap-12 flx-w m-b-8 txt-kale ${styles['order-detail__return-head']}`}>
                    <strong>Devolución #{returnView.serialNumber}</strong>
                    <span>{returnView.dateLabel} · {returnView.reasonLabel}</span>
                  </div>
                  <ul className={`m-0 p-0 txt-kale ${styles['order-detail__return-lines']}`}>
                    {returnView.lines.map((line) => (
                      <li key={line.productId}>
                        {line.productName} × {line.quantity}
                      </li>
                    ))}
                  </ul>
                  {returnView.notes !== undefined && (
                    <p className={`m-h-0 m-b-0 m-t-8 txt-light-kale ${styles['order-detail__return-notes']}`}>{returnView.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
